/* eslint-disable @typescript-eslint/no-explicit-any */
import Button from '@/components/common/Button';
import {
  AspectRatio,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Text,
  useDisclosure,
  VStack,
  Skeleton,
  SkeletonText,
  Box,
} from '@chakra-ui/react';
import Field from './Field';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetProductDetail } from '@/services/manageProduct';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { useState } from 'react';
import CustomImageSlider from '@/components/common/ImageSlider';
import { getCurrencySymbol } from '@/utils/currencyUtils';

interface SelectedImagesState {
  images: string[];
  index: number;
}

const ProductDetail = () => {
  const { viewProductId: id } = useParams<string>();
  const { data: productDetail, isLoading } = useGetProductDetail(id ?? '');
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImages, setSelectedImages] = useState<SelectedImagesState>({
    images: [],
    index: 0,
  });
  const handleImageClick = () => {
    onOpen();
  };

  const renderSkeletonField = (label: string, width = '150px') => (
    <GridItem>
      <VStack align="start" gap={1}>
        <Text color={'text.secondary'} fontSize={'xs'}>
          {label}
        </Text>
        <SkeletonText bg={'button.disable'} noOfLines={1} width={width} />
      </VStack>
    </GridItem>
  );

  const renderSkeletonCategory = () => (
    <VStack gap={4} alignItems={'start'} flex="1">
      <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
        Category Details
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
        {renderSkeletonField('Category')}
        {renderSkeletonField('Sub-category')}
      </Grid>
    </VStack>
  );

  const renderSkeletonProductDetails = () => (
    <VStack gap={4} alignItems={'start'} flex="1">
      <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
        Product Details
      </Text>
      <Grid templateColumns="repeat(4, 1fr)" gap={2} rowGap={4} width="100%">
        {renderSkeletonField('Name', '100%')}
        {renderSkeletonField('Brand', '100%')}
        {renderSkeletonField('Fabric', '100%')}
        {renderSkeletonField('Discount Percentage')}
        {renderSkeletonField('Short Description', '100%')}
        {renderSkeletonField('Product Specification', '100%')}
        {renderSkeletonField('Detailed Product Description', '100%')}
        {renderSkeletonField('Additional Information', '100%')}
        {renderSkeletonField('Collection', '100%')}
        {renderSkeletonField('Popularity')}
        {renderSkeletonField('Stitch Time', '100%')}
        <GridItem>
          <Text color={'text.secondary'} fontSize={'xs'} mb={'2'}>
            Video
          </Text>
          <Skeleton height="200px" borderRadius="md" />
        </GridItem>
      </Grid>
    </VStack>
  );

  const renderSkeletonVariant = () => (
    <VStack
      gap={4}
      alignItems={'start'}
      width={'100%'}
      p={4}
      border={'1px'}
      borderColor={'border.default'}
      bg={'background.white'}
      borderRadius={'lg'}
    >
      <HStack align="center" gap={3}>
        <Skeleton height="30px" width="30px" borderRadius="md" />
        <SkeletonText noOfLines={1} width="120px" />
      </HStack>
      <Grid templateColumns="repeat(1, 1fr)" gap={2} rowGap={4} width="100%">
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonText key={i} noOfLines={1} width="80px" />
          ))}
        </Grid>
        <VStack alignItems={'start'}>
          <Text color={'text.secondary'} fontSize={'xs'}>
            Media
          </Text>
          <Flex gap={4} direction={{ base: 'column', lg: 'row' }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height="200px" width="200px" borderRadius="lg" />
            ))}
          </Flex>
        </VStack>
      </Grid>
    </VStack>
  );

  const renderSkeletonCustomize = () => (
    <VStack gap={4} alignItems={'start'} flex="1">
      <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
        Customise Size
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
        {renderSkeletonField('Customize Price')}
        {renderSkeletonField('Customize Weight(kg)')}
        {renderSkeletonField('Style Fields', '100%')}
        <GridItem colSpan={2}>
          <Text color={'text.secondary'} fontSize={'xs'} mb={'2'}>
            Size Guide
          </Text>
          <Skeleton height="300px" borderRadius="md" />
        </GridItem>
      </Grid>
    </VStack>
  );

  const renderSkeletonAddOns = () => (
    <VStack gap={4} alignItems={'start'} flex="1">
      <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
        Add-Ons
      </Text>
      {[1, 2, 3, 4].map((i) => (
        <HStack
          key={i}
          p={3}
          border="1px"
          borderColor="border.default"
          borderRadius="lg"
          bg="background.white"
          width="100%"
          alignItems="center"
          spacing={4}
        >
          <Skeleton height="60px" width="60px" borderRadius="md" />
          <VStack align="start" flex="1" spacing={1}>
            <SkeletonText noOfLines={1} width="200px" />
            <SkeletonText noOfLines={1} width="150px" />
          </VStack>
        </HStack>
      ))}
    </VStack>
  );

  return (
    <Flex direction="column" gap={5} m={4} fontFamily={'primary'}>
      <HStack
        p={4}
        border={'1px solid'}
        bg={'#FFFFFF'}
        borderRadius={'lg'}
        borderColor={'border.default'}
        justifyContent={'space-between'}
        alignItems={'start'}
        width="100%"
      >
        {isLoading ? (
          renderSkeletonCategory()
        ) : (
          <VStack gap={4} alignItems={'start'} flex="1">
            <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
              Category Details
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
              <Field label="category" value={productDetail?.category?.name ?? '-'} />
              <Field label="sub-category" value={productDetail?.sub_category?.name ?? '-'} />
            </Grid>
          </VStack>
        )}
        <Button
          ml="auto"
          fontSize={'md'}
          borderColor={'brand.primary.DEFAULT'}
          fontWeight={'semibold'}
          bg={'#fff'}
          color={'brand.primary.DEFAULT'}
          border={'2px'}
          fontFamily={'secondary'}
          _hover={{
            color: '#fff',
            border: '2px',
            borderColor: 'brand.primary.DEFAULT',
            bg: 'brand.primary.DEFAULT',
          }}
          onClick={() => {
            if (id) {
              navigate(NAVIGATION_ROUTES.EDIT_PRODUCT.replace(':editProductId', id));
            }
          }}
          isDisabled={isLoading}
        >
          Edit Product
        </Button>
      </HStack>

      <HStack
        p={4}
        border={'1px solid'}
        borderRadius={'lg'}
        bg={'#FFFFFF'}
        borderColor={'border.default'}
        justifyContent={'space-between'}
        alignItems={'start'}
        width="100%"
      >
        {isLoading ? (
          renderSkeletonProductDetails()
        ) : (
          <VStack gap={4} alignItems={'start'} flex="1">
            <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
              Product Details
            </Text>
            <Grid templateColumns="repeat(4, 1fr)" gap={2} rowGap={4} width="100%">
              <Field label="name" value={productDetail?.name ?? '-'} />
              <Field label="brand" value={productDetail?.brand?.name ?? '-'} />
              <Field label="fabric" value={productDetail?.fabric?.name ?? '-'} />
              <Field
                label="discount Percentage"
                value={`${productDetail?.discount_percent ?? 0}%`}
              />
              <Field label="Short Description" value={productDetail?.description ?? '-'} />
              <Field
                label="Product Specification"
                value={productDetail?.product_specification ?? '-'}
              />
              <Field
                label="Detailed Product Description"
                value={productDetail?.detailed_product_description ?? '-'}
              />
              <Field
                label="Additional Information"
                value={productDetail?.additional_information ?? '-'}
              />
              <Field label="Collection" value={productDetail?.collection?.name ?? '-'} />
              <Field label="Popularity" value={productDetail?.popularity?.toString() ?? '0'} />
              <Field
                label="Stitch Time"
                value={productDetail?.preparation_days?.toString() ?? '0'}
              />
              <GridItem>
                <Text color={'text.secondary'} fontSize={'xs'}>
                  Video
                </Text>
                {productDetail?.video ? (
                  <AspectRatio ratio={4 / 3}>
                    <video
                      width="100%"
                      controls
                      style={{ borderRadius: '12px', objectFit: 'contain' }}
                    >
                      <source src={productDetail.video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                ) : (
                  <Text>-</Text>
                )}
              </GridItem>
            </Grid>
          </VStack>
        )}
      </HStack>

      <HStack
        p={4}
        border={'1px solid'}
        borderRadius={'lg'}
        borderColor={'border.default'}
        justifyContent={'space-between'}
        alignItems={'start'}
        bg={'#FFFFFF'}
        width="100%"
      >
        <VStack gap={4} alignItems={'start'} width={'100%'}>
          <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
            Variant Details
          </Text>
          {isLoading ? (
            renderSkeletonVariant()
          ) : productDetail?.product_variants?.length ? (
            (() => {
              const groupedByColor = productDetail.product_variants.reduce(
                (acc, variant) => {
                  const colorKey = `${variant.color}_${variant.value}`;
                  if (!acc[colorKey]) {
                    acc[colorKey] = {
                      color: variant.color,
                      value: variant.value,
                      images: variant.images || [],
                      sizes: [],
                    };
                  }
                  acc[colorKey].sizes.push({
                    size: variant?.size?.size ?? '-',
                    variant_price: variant?.variant_price ?? '-',
                    variant_weight: variant?.variant_weight ?? '-',
                    stock_quantity: variant?.stock_quantity?.toString() ?? '-',
                    sku: variant?.sku ?? '-',
                  });
                  return acc;
                },
                {} as Record<string, any>,
              );

              return Object.values(groupedByColor).map((colorGroup: any, index) => (
                <VStack
                  key={`${colorGroup.color}_${index}`}
                  gap={4}
                  alignItems={'start'}
                  width={'100%'}
                  p={4}
                  border={'1px'}
                  borderColor={'border.default'}
                  bg={'background.white'}
                  borderRadius={'lg'}
                >
                  <HStack align="center" gap={3}>
                    <Box
                      w="30px"
                      h="30px"
                      bg={colorGroup.value}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.300"
                    />
                    <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
                      {colorGroup.color}
                    </Text>
                  </HStack>

                  <Grid templateColumns="repeat(1, 1fr)" gap={2} rowGap={4} width="100%">
                    <VStack alignItems={'start'}>
                      <Text color={'text.secondary'} fontSize={'xs'}>
                        Media
                      </Text>
                      <Flex gap={4} direction={{ base: 'column', lg: 'row' }}>
                        {colorGroup.images?.length ? (
                          colorGroup.images.map((image: any) => (
                            <Image
                              key={image.id}
                              src={image?.image ?? ''}
                              h={'200px'}
                              w={'200px'}
                              objectFit={'cover'}
                              objectPosition={'top'}
                              borderRadius={'lg'}
                              cursor={'pointer'}
                              onClick={() => {
                                setSelectedImages({
                                  images: colorGroup.images.map((img: any) => img.image),
                                  index:
                                    colorGroup.images.findIndex(
                                      (img: any) => img.image === image.image,
                                    ) ?? 0,
                                });
                                handleImageClick();
                              }}
                            />
                          ))
                        ) : (
                          <Text color="text.secondary" fontSize="sm">
                            -
                          </Text>
                        )}
                      </Flex>
                    </VStack>

                    <VStack alignItems={'start'} spacing={3}>
                      <Text color={'text.secondary'} fontSize={'xs'} fontWeight={'medium'}>
                        Sizes
                      </Text>
                      {colorGroup.sizes.map((sizeData: any, sizeIdx: number) => (
                        <Grid
                          key={sizeIdx}
                          templateColumns="repeat(5, 1fr)"
                          gap={2}
                          width="100%"
                          p={3}
                          border={'1px'}
                          borderColor={'border.default60'}
                          borderRadius={'md'}
                          bg={'background.secondary'}
                        >
                          <Field label={'Size'} value={sizeData.size} />
                          <Field
                            label={'Variant Price'}
                            value={`${getCurrencySymbol(productDetail?.currency || 'NPR')}${sizeData.variant_price}`}
                          />
                          <Field label={'Weight(kg)'} value={sizeData.variant_weight} />
                          <Field label={'Stock Quantity'} value={sizeData.stock_quantity} />
                          <Field label={'SKU'} value={sizeData.sku} />
                        </Grid>
                      ))}
                    </VStack>
                  </Grid>
                </VStack>
              ));
            })()
          ) : (
            <Text color="text.secondary" fontSize="sm">
              No variants available
            </Text>
          )}
        </VStack>
      </HStack>

      {isLoading ? (
        <HStack
          p={4}
          border={'1px solid'}
          borderRadius={'lg'}
          borderColor={'border.default'}
          bg={'#FFFFFF'}
          justifyContent={'space-between'}
          alignItems={'start'}
          width="100%"
        >
          {renderSkeletonCustomize()}
        </HStack>
      ) : productDetail?.is_customizable ? (
        <HStack
          p={4}
          border={'1px solid'}
          borderRadius={'lg'}
          borderColor={'border.default'}
          bg={'#FFFFFF'}
          justifyContent={'space-between'}
          alignItems={'start'}
          width="100%"
        >
          <VStack gap={4} alignItems={'start'} flex="1">
            <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
              Customise Size
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={2} width="100%">
              <Field
                label="Customize Price"
                value={`${getCurrencySymbol(productDetail?.currency || 'NPR')}${productDetail?.customized_price ? Number(productDetail.customized_price).toFixed(2) : '-'} `}
              />
              <Field
                label="Customize Weight(kg)"
                value={`${productDetail?.customized_weight ?? '-'} `}
              />
              <Field
                label="Style Fields"
                value={productDetail?.style_fields?.map((field) => field.name).join(', ') || '-'}
              />
              <GridItem colSpan={2}>
                <Text color={'text.secondary'} fontSize={'xs'} mb={'2'}>
                  Size Guide
                </Text>
                {productDetail?.size_image ? (
                  <Image
                    src={productDetail.size_image}
                    alt="Size Guide"
                    borderRadius="md"
                    maxH="150px"
                    objectFit="contain"
                  />
                ) : (
                  <Text>-</Text>
                )}
              </GridItem>
            </Grid>
          </VStack>
        </HStack>
      ) : null}

      {isLoading ? (
        <HStack
          p={4}
          border={'1px solid'}
          borderRadius={'lg'}
          bg={'#FFFFFF'}
          borderColor={'border.default'}
          justifyContent={'space-between'}
          alignItems={'start'}
          width="100%"
        >
          {renderSkeletonAddOns()}
        </HStack>
      ) : productDetail?.is_add_on && productDetail?.add_ons_data?.length ? (
        <HStack
          p={4}
          border={'1px solid'}
          borderRadius={'lg'}
          bg={'#FFFFFF'}
          borderColor={'border.default'}
          justifyContent={'space-between'}
          alignItems={'start'}
          width="100%"
        >
          <VStack gap={4} alignItems={'start'} flex="1" width="100%">
            <Text fontSize="md" color={'text.primary'} fontWeight={'semibold'}>
              Add-Ons
            </Text>

            {productDetail?.add_ons_data?.length ? (
              Object.entries(
                productDetail.add_ons_data.reduce(
                  (
                    groups: Record<string, typeof productDetail.add_ons_data>,
                    addOn: (typeof productDetail.add_ons_data)[number],
                  ) => {
                    const type =
                      typeof addOn.add_on_field === 'object'
                        ? addOn.add_on_field?.name ?? 'Unknown'
                        : 'Unknown';
                    if (!groups[type]) groups[type] = [];
                    groups[type].push(addOn);
                    return groups;
                  },
                  {} as Record<string, typeof productDetail.add_ons_data>,
                ),
              ).map(([type, addOns]: [string, typeof productDetail.add_ons_data]) => (
                <VStack key={type} align="stretch" spacing={3} width="100%">
                  <Text fontSize="sm" fontWeight="medium" color="text.primary">
                    {type}
                  </Text>
                  <VStack align="stretch" spacing={3} pl={4}>
                    {addOns.map((addOn: (typeof productDetail.add_ons_data)[number]) => (
                      <HStack
                        key={addOn.id}
                        p={3}
                        border="1px"
                        borderColor="border.default"
                        borderRadius="lg"
                        bg="background.white"
                        alignItems="center"
                        spacing={4}
                        width="100%"
                      >
                        <Box flexShrink={0}>
                          {addOn?.images?.length ? (
                            <Image
                              src={addOn.images[0]?.image_small ?? ''}
                              boxSize="60px"
                              objectFit="cover"
                              borderRadius="md"
                              cursor="pointer"
                              onClick={() => {
                                setSelectedImages({
                                  images: addOn.images!.map((img) => img.image),
                                  index: 0,
                                });
                                handleImageClick();
                              }}
                            />
                          ) : (
                            <Box boxSize="60px" bg="gray.100" borderRadius="md" />
                          )}
                        </Box>

                        <HStack
                          flex="1"
                          spacing={8}
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={4}
                        >
                          <Field label="Name" value={addOn.display_name ?? '-'} />
                          <HStack align="center" gap={2} minW="120px">
                            <Box
                              w="24px"
                              h="24px"
                              bg={addOn.color?.hex_code}
                              borderRadius="sm"
                              border="1px solid"
                              borderColor="gray.300"
                            />
                            <Text fontSize="sm">{addOn.color?.name ?? '-'}</Text>
                          </HStack>
                          <Field label="Desc" value={addOn.description ?? '-'} />
                          <Field
                            label="Price"
                            value={`${getCurrencySymbol(productDetail?.currency || 'NPR')}${Number(addOn.price).toFixed(2)}`}
                          />
                          <Field label="Weight" value={`${addOn.weight ?? '-'} kg`} />
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              ))
            ) : (
              <Text color="text.secondary" fontSize="sm">
                No add-ons available
              </Text>
            )}
          </VStack>
        </HStack>
      ) : null}

      <Modal isOpen={isOpen} onClose={onClose} size={'4xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader></ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CustomImageSlider images={selectedImages.images} initialIndex={selectedImages.index} />
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ProductDetail;
